import { get } from "lodash"
import createModule from "../createModule"
import view from "./view"
import { Creators } from "./model"
import { Creators as PortfolioRootCreators } from "modules/portfolio-root/model"
import { Creators as ProfileCreators } from "model/profileModal"
import { computeSecurityProfilePositionSet } from "helpers/securities"
import { getSegment } from "modules/analyze-header/selectors/segment"
import { getSelectedDate, getStartDate, getEndDate, getDriftDates } from "modules/security-profile/selectors/dates"
import { TOP_POSITION_COUNT } from "helpers/analyzeConstants"
import { generateClassificationQueryForContributors, getClassifications } from "helpers/classification"
import { getDetails } from "./selectors/details"
import { getCategoryItemsSubset, getFilteredData } from "./selectors/data"
import { getFirstHistoricalBetaDate } from "./selectors/beta"
import { hasReferenceEquity, getContributorsSummaryText, getSortedContributors } from "./selectors/concentration"
import { getIsActive } from "./selectors"
import { getDisplayType } from "./selectors/items"
import { getFileName } from "./selectors/file"
import { getFilteredOptions } from "./selectors/options"
import { betasAreMissing, betasSubQuery } from "selectors/missingDataFiles"
import { getIsOpPro } from "modules/account-root/selectors"

export default createModule({
  view,
  externalProps: (state, props) => ({
    account: state.global.selected.account,
    driftDates: getDriftDates(state, props),
    startDate: getStartDate(state, props),
    endDate: getEndDate(state, props),
    selectedDate: getSelectedDate(state, props),
    segment: getSegment(state, props),
    missingDataFiles: get(props, "missingDataFiles"),
    classifications: getClassifications(state, props),
    isActive: getIsActive(state, props),
    contributorsGrouping: props.contributorsGrouping,
  }),
  required: ["account", "modelId", "missingDataFiles"],
  data: [{
    resource: "securityBetaHistory",
    url: ({ modelId, security: { assetClass, ticker, mic, modelProviderId }, base, driftDates, startDate, endDate, missingDataFiles }) => {
      if (betasAreMissing(missingDataFiles, modelId)) { return null }
      const from = startDate < driftDates.startDate ? startDate : driftDates.startDate
      const to = driftDates.endDate

      if (base && !base.type) {
        base = {
          dates: [{
            date: from,
            ...base,
          }],
        }
      }

      return {
        id: `securityBetaHistory-${modelId}${ticker}${mic}${startDate}${endDate}${JSON.stringify(base)}`,
        query: `query ($modelId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $from: Date!, $to: Date!) {
          model (id: $modelId) {
            simulation(positionSet: $positionSet, base: $base, from: $from, to: $to, interval: AUTO) {
              ${betasSubQuery(missingDataFiles, ["date"])}
            }
          }
        }`,
        variables: {
          modelId,
          positionSet: computeSecurityProfilePositionSet({ assetClass, ticker, mic, modelProviderId, startDate }),
          base,
          from,
          to,
        },
      }
    },
  }, {
    resource: "securityConcentration",
    as: null,
    url: ({ modelId, security: { ticker, mic, assetClass, modelProviderId }, driftDates, startDate, endDate }) => {
      const from = startDate < driftDates.startDate ? startDate : driftDates.startDate
      const to = driftDates.endDate

      return {
        id: `securityConcentration-${modelId}${ticker}${mic}${startDate}${endDate}`,
        query: `query ($modelId: String!, $positionSet: PositionSetInput!, $from: Date!, $to: Date!) {
          model(id: $modelId) {
            simulation(positionSet: $positionSet, from: $from, to: $to, interval: AUTO) {
              composition(scaleFormat: PERCENT_EQUITY_GMV) {
                date
                gmv
                referenceEquity
                positionsCount
                concentration {
                  max
                  top(count: ${TOP_POSITION_COUNT})
                }
              }
            }
          }
        }`,
        variables: {
          modelId,
          positionSet: computeSecurityProfilePositionSet({ assetClass, ticker, mic, modelProviderId, startDate }),
          from,
          to,
        },
      }
    },
  }, {
    resource: "securityConcentrationContributors",
    as: null,
    url: ({ modelId, security: { ticker, mic, assetClass, modelProviderId }, selectedDate, classifications }) => {
      return {
        id: `securityConcentrationContributors-${modelId}${ticker}${mic}${selectedDate}${JSON.stringify(classifications)}`,
        query: `query ($modelId: String!, $positionSet: PositionSetInput!, $date: Date!) {
          model (id: $modelId) {
            simulation (positionSet: $positionSet, from: $date, to: $date) {
              composition(scaleFormat: PERCENT_EQUITY_GMV) {
                date
                gmv
                referenceEquity
                positionsCount
                positions {
                  id
                  economicExposure
                  country
                  description
                  ${generateClassificationQueryForContributors("GICS", classifications)}
                  currency
                  assetClass
                  modelProviderId
                }
              }
            }
          }
        }`,
        variables: {
          modelId,
          date: selectedDate,
          positionSet: computeSecurityProfilePositionSet({ assetClass, ticker, mic, modelProviderId, startDate: selectedDate }),
        },
      }
    },
  }],
  mapStateToProps: (state, props) => ({
    data: getFilteredData(state, props),
    contributorsSummaryText: getContributorsSummaryText(state, props),
    contributors: getSortedContributors(state, props),
    fileName: getFileName(state, props),
    items: getCategoryItemsSubset(state, props),
    options: getFilteredOptions(state, props),
    details: getDetails(state, props),
    firstDate: getFirstHistoricalBetaDate(state, props),
    displayType: getDisplayType(state, props),
    isOpPro: getIsOpPro(state, props),
    refEquityOverride: hasReferenceEquity(state, props),
    ...state.modules.securityProfileComposition,
  }),
  actions: {
    setDate: PortfolioRootCreators.setProfileDate,
    resetDate: PortfolioRootCreators.resetProfileDate,
    openSecurityProfile: ProfileCreators.openSecurityProfile,
    ...Creators,
  },
})
